import { useMutation, useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import { Button, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DeleteWithConfirmation } from "components/DeleteWithConfirmation";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document/AddEditDocumentDialog";
import { DocumentType } from "generated-graphql/graphql";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import {
  ATTACH_DOCUMENT_TO_HARMONIZED_VIOLATION,
  DETACH_DOCUMENT_FROM_HARMONIZED_VIOLATION,
  HARMONIZED_VIOLATION_DOCUMENTS,
} from "hooks/useHarmonizedViolations";
import { useState } from "react";
import { downloadFromUrl } from "util/download";

import { useCanAttachViolationDocument } from "./useCanAttachViolationDocument";
import { SectionHeading } from "./violationDisplay";

/**
 * One shared row shape for both surfaces: the Documents list filters out removed
 * rows, while the per-comment chips include them so a removed file still reads
 * as removed on the comment that introduced it.
 *
 * `document` is null either when the row is a tombstone (removed, or its
 * underlying Document was hard-deleted elsewhere in the app — e.g. the
 * facility profile) — `documentTitle` / `documentFileExtension` are the
 * attach-time snapshot that lets the chip still name the file in either case.
 */
export type ViolationAttachment = {
  id: string;
  addedByCommentId?: string | null;
  deleted: boolean;
  documentTitle?: string | null;
  documentFileExtension?: string | null;
  document?: { id: string; title?: string | null; fileExtension?: string | null } | null;
};

/** Prefers the live document's title, falling back to the attach-time
 * snapshot, then the file extension — shared by the Documents list, the
 * per-comment chips, and the composer's pending attachment chips. */
export function attachmentLabel(row: {
  document?: { title?: string | null; fileExtension?: string | null } | null;
  documentTitle?: string | null;
  documentFileExtension?: string | null;
}): string {
  const ext = row.document?.fileExtension ?? row.documentFileExtension ?? "file";
  return row.document?.title ?? row.documentTitle ?? `Document.${ext}`;
}

/**
 * The chip row under a single comment. Attachment chips come from the same query
 * that backs the Documents section, so a comment's attachment can never disagree
 * with the violation's list; `beforeChips` lets a caller prepend a
 * non-attachment chip (the "Imported from Airtable" marker) into the same row,
 * which keeps one Stack and avoids the double spacing a nested one would add.
 */
export function ViolationAttachmentChips({
  rows,
  beforeChips,
}: {
  rows: ViolationAttachment[];
  beforeChips?: React.ReactNode;
}) {
  if (rows.length === 0 && !beforeChips) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }} alignItems="center">
      {beforeChips}
      {rows.map((row) => {
        const isTombstone = row.deleted || !row.document;
        return (
          <Chip
            key={row.id}
            size="small"
            variant="outlined"
            label={isTombstone ? `Removed: ${attachmentLabel(row)}` : attachmentLabel(row)}
            sx={isTombstone ? { textDecoration: "line-through", opacity: 0.6 } : undefined}
          />
        );
      })}
    </Stack>
  );
}

export function ViolationDocumentsSection({
  tenantId,
  violationId,
  facilityId,
}: {
  tenantId: string;
  violationId: string;
  /** Seeded onto the attach dialog so the resulting Document gets a
   * FacilityDocument row — without it, a facility-scoped user can upload an
   * attachment and never be authorized to download it (getDocumentDownloadLink
   * requires sharing a facility). Also makes the attachment discoverable from
   * the facility profile. */
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { canAttach, blockedReason } = useCanAttachViolationDocument(facilityId);
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  const { data } = useQuery(HARMONIZED_VIOLATION_DOCUMENTS, {
    variables: { tenantId, harmonizedViolationId: violationId },
  });

  const [attachDocument] = useMutation(ATTACH_DOCUMENT_TO_HARMONIZED_VIOLATION, {
    refetchQueries: [HARMONIZED_VIOLATION_DOCUMENTS],
    awaitRefetchQueries: true,
  });
  const [detachDocument, { loading: detachLoading }] = useMutation(
    DETACH_DOCUMENT_FROM_HARMONIZED_VIOLATION,
    {
      refetchQueries: [HARMONIZED_VIOLATION_DOCUMENTS],
      awaitRefetchQueries: true,
    }
  );

  const rows = data?.harmonizedViolationDocuments ?? [];
  const live = rows.filter((row) => !row.deleted);

  const onDetach = async (id: string) => {
    try {
      await detachDocument({ variables: { tenantId, id } });
      alerts.success("Document removed");
    } catch (e) {
      alerts.error("Could not remove document", e);
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SectionHeading>Documents</SectionHeading>
        <Tooltip title={blockedReason ?? ""}>
          {/* A disabled button fires no events, so the span carries the tooltip. */}
          <span>
            <Button
              size="small"
              disabled={!canAttach}
              onClick={() => {
                setAddEditDialogState({
                  ...defaultAddEditDocumentDialogState,
                  open: true,
                  mode: EditMode.ADD,
                  step: FormState.UPLOAD,
                  facilities: [{ id: facilityId }],
                });
              }}
            >
              Attach document
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {live.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No documents attached.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {live.map((row) => {
            // A live (not detached) row can still lack a document: the
            // underlying file was hard-deleted elsewhere (e.g. facility
            // profile). Render it like a tombstone — no download, detach
            // still allowed as cleanup.
            const document = row.document;
            return (
              <Stack
                key={row.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="body2"
                  sx={document ? undefined : { textDecoration: "line-through", opacity: 0.6 }}
                >
                  {document ? attachmentLabel(row) : `Removed: ${attachmentLabel(row)}`}
                </Typography>
                <Stack direction="row">
                  {document && (
                    <IconButton
                      size="small"
                      aria-label={`Download ${attachmentLabel(row)}`}
                      onClick={async () => {
                        const { data: linkData } = await getDocumentDownloadLink({
                          variables: { id: document.id },
                        });
                        if (linkData?.getDocumentDownloadLink) {
                          downloadFromUrl(linkData.getDocumentDownloadLink);
                        } else {
                          alerts.error("Could not get a download link for this document");
                        }
                      }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  )}
                  <DeleteWithConfirmation
                    tooltip={`Remove ${attachmentLabel(row)}`}
                    header="Remove this document?"
                    msg="This detaches the file from the violation. Re-attaching it later uploads a new copy — this does not undo."
                    loading={detachLoading}
                    onConfirm={() => onDetach(row.id)}
                  />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      {addEditDialogState.open && (
        <AddEditDocumentDialog
          tenantId={tenantId}
          dialogState={addEditDialogState}
          setDialogState={setAddEditDialogState}
          documentTypeOptions={[DocumentType.Other]}
          showFacilityPicker={false}
          onSubmit={async (document) => {
            try {
              await attachDocument({
                variables: {
                  tenantId,
                  harmonizedViolationId: violationId,
                  documentId: document.id,
                },
              });
              alerts.success("Document attached");
              setAddEditDialogState(defaultAddEditDocumentDialogState);
            } catch (e) {
              alerts.error("Could not attach document", e);
            }
          }}
          refetchQueries={[]}
        />
      )}
    </Stack>
  );
}
